import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { useTheme, Box, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { forwardRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { HFilledTextField } from '../TextField';
import { normalizeAutocompleteInputValue, wrapAutocompleteInputProps } from '../TextField/text-input-normalize';
import { useTranslation } from 'react-i18next';
import { listItemDark } from '@ui/style/style';
import InfoIconTooltip from '../Tooltips/InfoIconTooltip';
const SingleSelect = forwardRef(({ label, labelTooltipText, options, disabled = false, error, placeholder, name, loading = false, control, rules, bg = 'light', multiple = false, disableCloseOnSelect = false, limitTags, styles, showSelectedIcon = false, disableClearable = false, inputValue, onInputChange, trimWhitespace = true }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const memoizedOptions = useMemo(() => options, [options]);
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, labelTooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: labelTooltipText, size: 'small', variant: 'dark' }) }))] })), [label, labelTooltipText]);
    return (_jsx(Controller, { name: name, control: control, rules: rules, render: ({ field }) => {
            const selectedOptions = multiple
                ? memoizedOptions.filter((opt) => Array.isArray(field.value) && field.value.includes(opt.value))
                : null;
            const selectedOption = multiple
                ? null
                : (memoizedOptions.find((opt) => opt.value === field.value) ?? null);
            // Custom filter function to search by label, email, designation, and companyName
            const filterOptions = (options, { inputValue: rawInputValue }) => {
                const inputValue = trimWhitespace
                    ? normalizeAutocompleteInputValue(rawInputValue)
                    : rawInputValue;
                if (!inputValue)
                    return options;
                const searchLower = inputValue.toLowerCase();
                return options.filter((option) => {
                    const labelMatch = option.label?.toLowerCase().includes(searchLower);
                    const emailMatch = option.email?.toLowerCase().includes(searchLower);
                    const designationMatch = option.designation?.toLowerCase().includes(searchLower);
                    const companyMatch = option.companyName?.toLowerCase().includes(searchLower);
                    return labelMatch || emailMatch || designationMatch || companyMatch;
                });
            };
            return (_jsx(Autocomplete, { ...field, multiple: multiple, disableCloseOnSelect: multiple || disableCloseOnSelect, disableClearable: disableClearable, options: memoizedOptions, fullWidth: true, filterOptions: filterOptions, ...(inputValue !== undefined && { inputValue }), ...(onInputChange !== undefined && {
                    onInputChange: (event, value, reason) => onInputChange(event, trimWhitespace ? normalizeAutocompleteInputValue(value) : value, reason)
                }), ...(multiple && limitTags !== undefined && { limitTags }), sx: {
                    '& .MuiFilledInput-root .MuiAutocomplete-endAdornment': {
                        right: '3px' // Change this value as needed
                    }
                }, slotProps: {
                    paper: {
                        sx: {
                            backgroundColor: '#FFFFFFEB',
                            backdropFilter: 'blur(40px)',
                            color: (theme) => theme.palette.text.primary,
                            boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D',
                            '& .MuiAutocomplete-noOptions': {
                                color: (theme) => theme.palette.text.primary
                            }
                        }
                    }
                }, popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', color: bg === 'blue' ? theme.palette.common.white : theme.palette.grey[700] }), disabled: disabled, loading: loading, getOptionLabel: (option) => option?.label ?? '', getOptionKey: (option) => String(option.value), isOptionEqualToValue: (option, value) => {
                    // Handle both primitive and object values
                    if (value && typeof value === 'object' && 'value' in value) {
                        return option.value === value.value;
                    }
                    return option.value === value;
                }, value: multiple ? selectedOptions : selectedOption, onChange: (_, newValue) => {
                    if (multiple) {
                        const valuesArray = Array.isArray(newValue)
                            ? newValue.map((opt) => ('value' in opt ? opt.value : opt))
                            : [];
                        field.onChange(valuesArray);
                    }
                    else {
                        // Preserve empty string values, only convert undefined to null
                        const val = newValue;
                        field.onChange(val?.value !== undefined ? val.value : null);
                    }
                }, noOptionsText: t('labels.noOptions'), renderOption: (props, option) => {
                    const { key: _key, ...optionProps } = props;
                    return (_createElement("li", { ...optionProps, key: String(option.value), style: listItemDark },
                        _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, width: '100%' }, children: [option.icon &&
                                    (typeof option.icon === 'string' ? _jsx(Icon, { icon: option.icon }) : option.icon), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1 }, children: [_jsx(Typography, { variant: 'body2', sx: { color: 'inherit', lineHeight: 1.5 }, children: option.label }), (option.email || option.designation || option.companyName) && (_jsxs(Box, { sx: { mt: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }, children: [option.email && (_jsx(Typography, { variant: 'caption', sx: {
                                                        color: bg === 'grey8'
                                                            ? theme.palette.text.secondary
                                                            : theme.palette.common.white,
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.2
                                                    }, children: option.email })), (option.designation || option.companyName) && (_jsx(Typography, { variant: 'caption', sx: {
                                                        color: bg === 'grey8'
                                                            ? theme.palette.text.secondary
                                                            : theme.palette.common.white,
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.2
                                                    }, children: [option.designation, option.companyName]
                                                        .filter(Boolean)
                                                        .join(' • ') }))] }))] })] })));
                }, renderInput: (params) => {
                    const isGrey8 = bg === 'grey8' || bg === 'grey8Event';
                    const startIcon = !multiple && showSelectedIcon && selectedOption?.icon ? selectedOption.icon : null;
                    const startAdornment = startIcon != null ? (_jsx(Box, { sx: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            mr: 1,
                            '& img': { display: 'block' }
                        }, children: typeof startIcon === 'string' ? _jsx(Icon, { icon: startIcon }) : startIcon })) : null;
                    return (_jsx(HFilledTextField, { ...params, trimWhitespace: trimWhitespace, inputProps: wrapAutocompleteInputProps(params.inputProps, trimWhitespace), bg: bg === 'blue' ? 'blue' : isGrey8 ? 'grey8' : 'light', inputRef: ref, disabled: disabled, label: styledLabel, placeholder: placeholder || '', error: !!error, helperText: error, styles: styles ||
                            (!isGrey8
                                ? {
                                    '& .MuiFilledInput-root': {
                                        border: 'none !important',
                                        '&:before, &:after': {
                                            display: 'none !important'
                                        },
                                        '&.Mui-focused': {
                                            border: 'none !important'
                                        }
                                    }
                                }
                                : undefined), slotProps: {
                            input: {
                                ...params.InputProps,
                                disableUnderline: true,
                                startAdornment: startAdornment != null ? (_jsxs(_Fragment, { children: [startAdornment, params.InputProps.startAdornment] })) : (params.InputProps.startAdornment),
                                endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { color: 'inherit', size: 20 }), params.InputProps.endAdornment] }))
                            }
                        } }));
                } }));
        } }));
});
export default SingleSelect;
